import { CommonModule } from '@angular/common'
import { Component, Input, OnInit, signal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { ToastrService } from '@app/services/toastr.service'
import { AuthService } from '@auth/services/auth.service'
import { EventService } from '@events/services/event.service'
import { Subject, takeUntil } from 'rxjs'

/**
 * Represents a VCD capacity card
 */
@Component({
  selector: 'ngx-capacity-card',
  templateUrl: './capacity-card.component.html',
  styleUrls: ['./capacity-card.component.scss'],
  imports: [CommonModule, FormsModule, MatIconModule]
})
export class CapacityCardComponent implements OnInit {
  /**
   * Current checkin count
   */
  checkinCount = 0
  currentCheckinCount = signal(0)
  /**
   * Toggles display mode
   */
  show = false
  /**
   * Toggles settings
   */
  showSettings = false
  /**
   * Current event slug
   */
  @Input()
  eventSlug
  /**
   * Current workspace slug
   */
  @Input()
  workspaceSlug
  /**
   * Current event id
   */
  @Input()
  eventId
  /**
   * Zone model
   */
  _zone
  /**
   * Sets the current zone and polls the checking count
   */
  @Input()
  set zone(value) {
    this._zone = value
    this.currentCheckinCount.set(this.getZoneCheckinCount(value))
    this.pollCheckinCount()
  }
  /**
   * Gets the zone model
   */
  get zone() {
    return this._zone
  }
  /**
   * Main zone model
   */
  @Input()
  mainZone
  /**
   * List of zones
   */
  @Input()
  zones
  /**
   * Whether we are in authenticated mode
   */
  @Input()
  authenticated = false
  /**
   * Whether we are not in authenticated mode
   */
  @Input()
  singleMode = true
  /**
   * Is the component ready?
   */
  ready = false
  private _destroy$ = new Subject()

  /**
   * Creates the component and injects it's dependencies
   * @param eventService EventService
   * @param toastr ToastrService
   */
  constructor(private eventService: EventService, private toastr: ToastrService, private dialog: MatDialog, public authService: AuthService) {}
  /**
   * Total capacity
   */
  capacityNum: any = ''
  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.pollCheckinCount()
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Polls the checkins count
   */
  pollCheckinCount() {
    this.zone.slug
      ? this.eventService
          .pollCurrentAttendees(this.workspaceSlug, this.eventSlug, this.zone.slug, this.workspaceSlug + this.eventSlug + this.zone.slug)
          .pipe(takeUntil(this._destroy$))
          .subscribe((result: { count: number; identifier: string }) => {
            if (result.identifier === this.workspaceSlug + this.eventSlug + this.zone.slug) {
              this.checkinCount = result.count
              this.zone.checkinCount = result.count
              this.currentCheckinCount.set(result.count)
            }
          })
      : this.zones?.forEach((zone) => {
          if (zone != this.zone)
            this.eventService
              .pollCurrentAttendees(this.workspaceSlug, this.eventSlug, zone.slug, this.workspaceSlug + this.eventSlug + zone.slug)
              .pipe(takeUntil(this._destroy$))
              .subscribe((result: { count: number; identifier: string }) => {
                if (result.identifier === this.workspaceSlug + this.eventSlug + zone.slug) {
                  zone.checkinCount = result.count
                }
              })
        })
  }

  getZoneCheckinCount(zone) {
    return Object.prototype.hasOwnProperty.call(zone ?? {}, 'checkinCount') ? zone.checkinCount : this.checkinCount
  }

  getCurrentCheckinCount(zone = this.zone) {
    return zone === this.zone ? this.currentCheckinCount() : this.getZoneCheckinCount(zone)
  }
  /**
   * Toggles card details mode
   */
  viewCardDetails() {
    this.show = !this.show
  }
  /**
   * Gets the percentage of current checkin vs zone maximum capacity
   */
  getPercentage(zone = null) {
    return !zone
      ? parseInt(((this.getCurrentCheckinCount() * 100) / this.zone.max_capacity).toString(), 10)
      : parseInt(((zone.checkinCount * 100) / zone.max_capacity).toString(), 10)
  }
  /**
   * Gets a background color based on the percentage
   */
  getBackground(zone = null) {
    return this.getPercentage(zone) === 100 || this.getPercentage(zone) >= 100
      ? '#CE1126'
      : this.getPercentage(zone) < 100 && this.getPercentage(zone) >= 75
      ? '#F97017'
      : '#2CCD59'
  }
  /**
   * Toggles settings mode
   */
  showSettingsChanged() {
    this.showSettings = !this.showSettings
  }

  /**
   * Toggles settings mode on
   */
  showSetting() {
    this.showSettings = true
  }

  /**
   * Updates this zone capacity
   */
  updateCapacity() {
    if (this.capacityNum) {
      if (this.capacityNum > this.mainZone.max_capacity && this.mainZone.max_capacity !== 0 && this.zone.slug !== 'global') {
        this.toastr.error('Sub-zone capacity number could not be more than its parent capacity.', '')
        this.capacityNum = ''
      } else if (this.zone.slug === 'global') {
        this.zones.forEach((zone) => {
          if (this.capacityNum < zone.max_capacity && zone.slug !== 'global' && this.capacityNum !== 0) {
            this.toastr.error('Main zone capacity number could not be less than its subzones capacity.', '')
            this.capacityNum = ''
          } else {
            this.updateCapacityRequest()
          }
        })
      } else {
        this.updateCapacityRequest()
      }
    }
  }

  /**
   * Zone update capacity integrtion
   */
  updateCapacityRequest() {
    this.eventService
      .updateCapacity(this.eventId, this.capacityNum, this.zone)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response: any) => {
          this.zone = response
          const items = this.zones
          items.forEach((item) => {
            if (item.slug === response.slug) {
              this.zones[items.indexOf(item)] = response
              this.toastr.success('You have updated the capacity successfully', '')
              this.capacityNum = ''
            }
          })
        },
        (error) => {
          this.toastr.error('Failed to update the capacity', '')
        }
      )
  }

  /**
   * Opens the force check-out confirmation dialog and, on confirm, optimistically
   * clears the count before calling the API
   */
  openForceCheckOutConfirm() {
    const count = this.zone.checkinCount ?? this.checkinCount
    const ref = this.dialog.open(DialogComponent, {
      data: {
        headerText: 'Force Check-Out',
        messageText: `Are you sure you want to checkout ${count} ${count === 1 ? 'user' : 'users'} inside zone "${this.zone.name}"?`,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        confirmButtonClass: 'delete'
      }
    })
    ref
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        if (result === 'confirm') {
          this.checkinCount = 0
          this.zone.checkinCount = 0
          this.currentCheckinCount.set(0)
          this.eventService
            .forceCheckOut(this.workspaceSlug, this.eventSlug, this.zone.slug)
            .pipe(takeUntil(this._destroy$))
            .subscribe(
              () => {
                this.toastr.success('All attendees have been checked out successfully', '')
              },
              () => {
                this.toastr.error('Failed to force check-out attendees', '')
              }
            )
        }
      })
  }

  /**
   * Show zone settings icon
   */
  showSettingsIcon() {
    return this.singleMode ? false : this.authenticated
  }
}
